import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";

import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Checkbox } from "@shared/ui/checkbox";
import { MoveRoutesDialog } from "./moveRoutesDialog";
import { Badge } from "@shared/ui/badge";
import {
  useGetMeterType,
  useGetRouteDetails,
  useUtilityServices,
} from "@features/mx/readCycleManagement/hooks";
import { RouteFiltersComponent } from "./filter";

interface RouteData {
  id: string;
  routeName: string;
  readType: string;
  utilityService: string;
  meters: number;
  premises: number;
}

interface RouteFilters {
  utilityService: string;
  readType: string;
}

export const RoutesTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cycle_id } = useParams();
  const navigate = useNavigate();
  const { data: utilities } = useUtilityServices();
  const { data: meterTypeData } = useGetMeterType();
  const utilityService = searchParams.get("utilityService") || "";
  const readType = searchParams.get("readType") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: routeDetails } = useGetRouteDetails(
    currentPage,
    PAGE_SIZE,
    cycle_id,
    utilityService,
    readType,
    searchParams.get("search"),
  );

  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [moveRoutesDialogOpen, setMoveRoutesDialogOpen] = useState(false);

  const allSelected = useMemo(() => {
    return (
      selectedRoutes.length === routeDetails?.results.length &&
      routeDetails?.results.length > 0
    );
  }, [selectedRoutes.length, routeDetails?.results.length]);

  const estimatedMeters = useMemo(() => {
    return selectedRoutes.reduce((total, routeId) => {
      const route = routeDetails?.results.find((r) => r.id === routeId);
      return total + (route ? route.meterCount : 0);
    }, 0);
  }, [selectedRoutes, routeDetails?.results]);

  const handleSelectRoute = (id: string, checked: boolean) => {
    setSelectedRoutes((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      } else {
        if (!prev.includes(id)) return prev;
        return prev.filter((routeId) => routeId !== id);
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      if (selectedRoutes.length !== routeDetails?.results.length) {
        setSelectedRoutes(routeDetails?.results.map((route) => route.id));
      }
    } else {
      if (selectedRoutes.length > 0) {
        setSelectedRoutes([]);
      }
    }
  };

  const handleOpenMoveRoutesDialog = () => {
    setMoveRoutesDialogOpen(true);
  };

  const handleMoveRoutes = (destinationCycleId: string) => {
    setSelectedRoutes([]);
  };

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  useEffect(() => {
    const totalItems = routeDetails?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [routeDetails?.count, currentPage]);

  const handleApplyFilters = (filters: RouteFilters) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(filters.utilityService && {
        utilityService: filters.utilityService,
      }),
      ...(filters.readType && { readType: filters.readType }),
    };

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.utilityService;
    delete newParams.readType;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(readType && { readType }),
      ...(utilityService && { utilityService }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const columns: TableColumn<any>[] = [
    {
      key: "select",
      header: (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => handleSelectAll(!!checked)}
          />
        </div>
      ),
      width: "w-5",
      type: "custom",
      sortable: false,
      render: (_, row: RouteData) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={selectedRoutes.includes(row.id)}
            onCheckedChange={(checked) => handleSelectRoute(row.id, !!checked)}
          />
        </div>
      ),
    },
    {
      key: "routeName",
      header: "Route Name",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Read Type",
      sortable: true,
    },
    {
      key: "utilityService",
      header: "Utility Service",
      sortable: true,
      render: (value: string) => <span>{value}</span>,
    },
    {
      key: "meterCount",
      header: "Resource",
      sortable: true,
    },
    {
      key: "premiseCount",
      header: "Premises",
      sortable: true,
      render: (value: string) => <span className="ml-5">{value}</span>,
    },
  ];

  // Define actions - use useMemo to prevent re-creation
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: (row: RouteData) => {
        navigate(`/mx/routeManagement/view/view-details/${row.id}`);
      },
      icon: <Eye className="h-4 w-4 ml-6" />,
    },
  ];

  // Use the filter component
  const filterPanel = (
    <RouteFiltersComponent
      utilityService={utilityService}
      readType={readType}
      utilities={utilities || []}
      meterTypes={meterTypeData || []}
      onApplyFilters={handleApplyFilters}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = utilityService || readType;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <PageHeader
            title="Routes"
            subtitle="Browse routes in this cycle with read type, utilities, and meter counts."
          />
        </div>

        {/* Display move routes button when routes are selected */}
        {selectedRoutes.length > 0 && (
          <div className="flex justify-end items-center gap-2">
            <Badge variant="outline">{selectedRoutes.length} routes</Badge>
            <Button onClick={handleOpenMoveRoutesDialog}>
              Move routes to other read cycle
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <GenericTable
          data={routeDetails?.results || []}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          emptyMessage="No routes found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search routes..."
          filterPanel={filterPanel}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {[!!utilityService, !!readType].filter(Boolean).length}{" "}
                  filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>

      {/* Move Routes Dialog */}
      <MoveRoutesDialog
        open={moveRoutesDialogOpen}
        onOpenChange={setMoveRoutesDialogOpen}
        selectedRoutes={selectedRoutes}
        onMoveRoutes={handleMoveRoutes}
        estimatedMeters={estimatedMeters}
      />
    </div>
  );
};
